/**
 * Puerto de transporte hacia el backend. En producción lo implementa el puente
 * seguro del preload (`window.fabianMeli`, ver Fase 7 Electron Shell). En navegador
 * o test se usa `MockTransport`. La UI NUNCA accede a Node/DB directamente.
 */
export interface BridgeResponse<T = unknown> {
  readonly ok: boolean;
  readonly result?: T;
  readonly error?: { readonly code: string; readonly message: string };
}

export interface Transport {
  invoke<T>(channel: string, payload?: unknown): Promise<BridgeResponse<T>>;
  subscribe(eventChannel: string, listener: (payload: unknown) => void): () => void;
}

interface FabianMeliBridge {
  invoke(channel: string, payload?: unknown): Promise<BridgeResponse>;
  subscribe(eventChannel: string, listener: (payload: unknown) => void): () => void;
}

declare global {
  interface Window {
    fabianMeli?: FabianMeliBridge;
  }
}

/** Devuelve el transporte del preload si está disponible (app empaquetada). */
export function getBridgeTransport(): Transport | null {
  const bridge = typeof window !== "undefined" ? window.fabianMeli : undefined;
  if (!bridge) return null;
  return {
    invoke: <T,>(channel: string, payload?: unknown) => bridge.invoke(channel, payload) as Promise<BridgeResponse<T>>,
    subscribe: (channel, listener) => bridge.subscribe(channel, listener),
  };
}

/**
 * Transporte híbrido: enruta al backend REAL los canales que este implementa (según
 * `isReal`) y al transporte de reserva (mock) el resto. Permite una "rebanada vertical"
 * — un motor con datos reales mientras las demás pantallas siguen con datos de ejemplo.
 */
export function makeHybridTransport(real: Transport, fallback: Transport, isReal: (channel: string, payload?: unknown) => boolean): Transport {
  return {
    invoke: <T,>(channel: string, payload?: unknown) => (isReal(channel, payload) ? real.invoke<T>(channel, payload) : fallback.invoke<T>(channel, payload)),
    subscribe: (channel, listener) => (isReal(channel) ? real.subscribe(channel, listener) : fallback.subscribe(channel, listener)),
  };
}

/** Transporte simulado: resuelve canales contra una tabla de handlers en memoria. */
export class MockTransport implements Transport {
  private readonly handlers = new Map<string, (payload: unknown) => unknown>();
  private readonly latencyMs: number;

  constructor(options: { latencyMs?: number } = {}) {
    this.latencyMs = options.latencyMs ?? 0;
  }

  on(channel: string, handler: (payload: unknown) => unknown): this {
    this.handlers.set(channel, handler);
    return this;
  }

  async invoke<T>(channel: string, payload?: unknown): Promise<BridgeResponse<T>> {
    if (this.latencyMs > 0) await new Promise((r) => setTimeout(r, this.latencyMs));
    const handler = this.handlers.get(channel);
    if (!handler) {
      return { ok: false, error: { code: "not_found", message: `Canal sin handler: ${channel}` } };
    }
    try {
      return { ok: true, result: handler(payload) as T };
    } catch (error) {
      return { ok: false, error: { code: "internal", message: (error as Error).message } };
    }
  }

  subscribe(): () => void {
    return () => {};
  }
}
